// Same-origin JSON client for API v1. Every request sends the session cookie;
// every mutation carries the CSRF token issued by GET /api/me.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Lost-update protection: the expected version was no longer current. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

export const CSRF_HEADER = 'x-csrf-token';

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

async function parseError(response: Response): Promise<ApiError> {
  let code = `http_${response.status}`;
  let message = response.statusText || 'The request failed.';
  try {
    const body: unknown = await response.json();
    if (isErrorEnvelope(body)) {
      code = body.error.code;
      message = body.error.message;
    }
  } catch {
    // Non-JSON error bodies (e.g. a proxy page) keep the HTTP status message.
  }
  return new ApiError(response.status, code, message);
}

function isErrorEnvelope(value: unknown): value is { error: { code: string; message: string } } {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const error = (value as { error: unknown }).error;
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as { code?: unknown }).code === 'string' &&
    typeof (error as { message?: unknown }).message === 'string'
  );
}

export async function request<T>(method: Method, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') {
    if (csrfToken === null) {
      throw new ApiError(401, 'csrf_missing', 'Your session is not ready. Reload the page and sign in again.');
    }
    headers[CSRF_HEADER] = csrfToken;
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'network_error', 'The research hub could not be reached. Check your connection and try again.');
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
};

/** Builds a query string, omitting empty values so URLs stay canonical. */
export function toQuery(params: Record<string, string | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value.trim() !== '') search.set(key, value.trim());
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

/** Protected raw asset URL. The ID is a database asset ID, never a filesystem path. */
export function assetUrl(assetId: string): string {
  return `/api/assets/${encodeURIComponent(assetId)}`;
}

export function exportDownloadUrl(exportId: string): string {
  return `/api/exports/${encodeURIComponent(exportId)}/download`;
}
